import React, { useState, useMemo } from 'react';
import './DateSelector.css';

export default function DateSelector({ slots, selectedDate, onSelectDate, partySize }) {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Safe date string extraction - handles any format
  const getDateStringFromSlot = (slot) => {
    try {
      if (!slot || !slot.startTime) return null;

      let dateObj;
      // Try to parse as Date if it's a string
      if (typeof slot.startTime === 'string') {
        dateObj = new Date(slot.startTime);
      } else if (slot.startTime instanceof Date) {
        dateObj = slot.startTime;
      } else {
        console.warn('Unknown slot.startTime format:', slot.startTime);
        return null;
      }

      if (isNaN(dateObj.getTime())) {
        console.warn('Invalid date parsed:', slot.startTime);
        return null;
      }

      // Format as YYYY-MM-DD
      const year = dateObj.getFullYear();
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      const day = String(dateObj.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (err) {
      console.error('Error parsing slot date:', err, slot);
      return null;
    }
  };

  // Get days with available slots
  const daysWithSlots = useMemo(() => {
    const daysSet = new Set();
    if (!slots || !Array.isArray(slots) || slots.length === 0) {
      console.warn('DateSelector: No slots provided', slots);
      return daysSet;
    }

    slots.forEach((slot, idx) => {
      const dateStr = getDateStringFromSlot(slot);
      if (dateStr) {
        daysSet.add(dateStr);
        if (idx === 0) console.log('📅 First slot:', slot, '→ dateStr:', dateStr);
      } else {
        if (idx === 0) console.error('📅 Failed to parse first slot:', slot);
      }
    });
    console.log('📅 Days with slots:', Array.from(daysSet));
    return daysSet;
  }, [slots]);

  // Get slots for a specific date
  const getSlotsForDate = (date) => {
    if (!date || !(date instanceof Date)) return [];
    if (!slots || !Array.isArray(slots)) return [];

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const targetDateStr = `${year}-${month}-${day}`;

    return slots.filter(slot => {
      const slotDateStr = getDateStringFromSlot(slot);
      return slotDateStr === targetDateStr;
    });
  };

  // Check if date has availability for party size
  const hasAvailability = (date) => {
    const slotsForDate = getSlotsForDate(date);
    return slotsForDate.some(slot => {
      const spotsAvailable = Math.max(0, slot.maxBookings - slot.currentBookings);
      return spotsAvailable >= partySize;
    });
  };

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const goToPreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  const handleDateClick = (day) => {
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    // Only allow selecting dates with available slots
    if (hasAvailability(date)) {
      onSelectDate(date);
    }
  };

  const monthName = currentMonth.toLocaleString('en-US', { month: 'long', year: 'numeric' });
  const daysInMonth = getDaysInMonth(currentMonth);
  const firstDay = getFirstDayOfMonth(currentMonth);
  const days = [];

  // Empty cells for days before month starts
  for (let i = 0; i < firstDay; i++) {
    days.push(null);
  }

  // Days of the month
  for (let day = 1; day <= daysInMonth; day++) {
    days.push(day);
  }

  const isDateSelected = (day) => {
    if (!selectedDate || day === null) return false;
    return (
      selectedDate.getFullYear() === currentMonth.getFullYear() &&
      selectedDate.getMonth() === currentMonth.getMonth() &&
      selectedDate.getDate() === day
    );
  };

  const isDateInPast = (day) => {
    if (day === null) return false;
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    return date < new Date();
  };

  return (
    <div className="date-selector">
      {/* Month Navigation */}
      <div className="calendar-header">
        <button onClick={goToPreviousMonth} className="nav-btn">◀</button>
        <h3>{monthName}</h3>
        <button onClick={goToNextMonth} className="nav-btn">▶</button>
      </div>

      {/* Day Headers */}
      <div className="calendar-weekdays">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <div key={day} className="weekday">{day}</div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="calendar-grid">
        {days.map((day, idx) => {
          // Create date object once for all checks
          const dateObj = day ? new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day) : null;
          const isPast = dateObj && isDateInPast(day);
          const hasSlots = dateObj && hasAvailability(dateObj);  // FIXED: Pass Date object, not day number
          const isSelected = isDateSelected(day);

          return (
            <button
              key={idx}
              className={`calendar-day ${day === null ? 'empty' : ''} ${isPast ? 'past' : ''} ${hasSlots ? 'available' : ''} ${isSelected ? 'selected' : ''}`}
              onClick={() => day && handleDateClick(day)}
              disabled={!day || isPast || !hasSlots}
              title={day && hasSlots ? `${getSlotsForDate(new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day)).length} slots available` : ''}
            >
              {day && (
                <>
                  <span className="day-number">{day}</span>
                  {hasSlots && <span className="availability-dot">●</span>}
                </>
              )}
            </button>
          );
        })}
      </div>

      {/* Legend */}
      <div className="calendar-legend">
        <div className="legend-item">
          <span className="legend-dot available">●</span> Available
        </div>
        <div className="legend-item">
          <span className="legend-dot empty">●</span> No slots
        </div>
      </div>
    </div>
  );
}
